// Binds the external transcript store, durable transport, selected renderer,
// route position bridge, and composer. Routes own persistence policy; this
// component owns expansion state and sequences restoration after catch-up.
import { useRouter } from '@tanstack/react-router';
import { OctagonX } from 'lucide-react';
import { motion, useReducedMotion } from 'motion/react';
import { usePostHog } from 'posthog-js/react';
import {
    Fragment,
    type ReactNode,
    type Ref,
    useCallback,
    useEffect,
    useImperativeHandle,
    useLayoutEffect,
    useMemo,
    useRef,
    useState,
    useSyncExternalStore,
} from 'react';

import { Button } from '@apps/web/components/ui/button';
import { EntityReferenceLabelsProvider } from '@apps/web/features/phoebe_chat_v3/api/entity_references';
import {
    createEventStreamClient,
    type EventStreamClientFailure,
    type EventStreamCycleState,
} from '@apps/web/features/phoebe_chat_v3/api/event_stream_client';
import { type StreamLineClassification } from '@apps/web/features/phoebe_chat_v3/api/ndjson_parser';
import {
    type EventChatClientIssue,
    recordEventChatClientIssue,
} from '@apps/web/features/phoebe_chat_v3/api/telemetry';
import {
    groupActivityOccurrences,
    holdPresentationsUntilAnswer,
    latestVisibleInputIsAssistantAnswer,
    partitionActiveWorkOccurrences,
    projectActivityRows,
} from '@apps/web/features/phoebe_chat_v3/model/activity';
import {
    type DoneLine,
    type EventStreamLine,
    type HarnessScope,
    type RunStateRead,
} from '@apps/web/features/phoebe_chat_v3/model/event_types';
import { applyStreamLine } from '@apps/web/features/phoebe_chat_v3/model/ingestion';
import { type MessageCreateRequest } from '@apps/web/features/phoebe_chat_v3/model/interaction_types';
import { selectNextStepsOffer } from '@apps/web/features/phoebe_chat_v3/model/next_steps';
import {
    type ActivityProjectionIssue,
    type ActivityProjectionRegistry,
    projectActivityOccurrences,
    type ProjectedActivityInput,
    projectQueuedUserMessages,
} from '@apps/web/features/phoebe_chat_v3/model/projection';
import {
    captureStopRequestBoundary,
    deriveResponsePhase,
    isTerminalResponseEvent,
    projectAnswerTail,
    projectStopRequests,
    responseEndedAfter,
    responsePhaseIsLive,
    responsePhaseWaitingFor,
    responseThinkingFollowsAnswer,
    type StopRequestBoundary,
} from '@apps/web/features/phoebe_chat_v3/model/response_phase';
import {
    type ConnectionState,
    type TranscriptState,
} from '@apps/web/features/phoebe_chat_v3/model/state';
import {
    createForegroundClock,
    createTranscriptStore,
    thinkingEvidenceTransitionInMs,
} from '@apps/web/features/phoebe_chat_v3/model/transcript_store';
import {
    type AgentProfile,
    type MessageSendPort,
} from '@apps/web/features/phoebe_chat_v3/profiles/agent_profile';
import { createInterruptController } from '@apps/web/features/phoebe_chat_v3/profiles/v3_phoebe_profile';
import {
    composeDefinitionModules,
    composeRendererRegistry,
} from '@apps/web/features/phoebe_chat_v3/registry/compose_registry';
import { generatedDefinitionModules } from '@apps/web/features/phoebe_chat_v3/registry/generated_registry_input';
import {
    type ProjectedTranscriptRow,
    type RendererRegistryShape,
    type SafeRenderContext,
} from '@apps/web/features/phoebe_chat_v3/registry/types';
import { CHAT_COLUMN_CLASS } from '@apps/web/features/phoebe_chat_v3/ui/chat_column';
import {
    Composer,
    type ComposerPrefill,
} from '@apps/web/features/phoebe_chat_v3/ui/composer';
import { HarnessScopeContext } from '@apps/web/features/phoebe_chat_v3/ui/harness_scope_context';
import {
    type LatestAssistantAnswer,
    LatestAssistantOccurrenceContext,
} from '@apps/web/features/phoebe_chat_v3/ui/latest_assistant_message_context';
import { composeMessageAttachments } from '@apps/web/features/phoebe_chat_v3/ui/message_attachments';
import {
    NextStepsOfferContext,
    type NextStepsOfferValue,
} from '@apps/web/features/phoebe_chat_v3/ui/next_steps_offer_context';
import {
    type ApprovalStack,
    ApprovalStackContext,
} from '@apps/web/features/phoebe_chat_v3/ui/primitives/approval_stack';
import { CardResize } from '@apps/web/features/phoebe_chat_v3/ui/primitives/card_transition';
import { ComposerFooter } from '@apps/web/features/phoebe_chat_v3/ui/primitives/composer_footer';
import { ConversationLoading } from '@apps/web/features/phoebe_chat_v3/ui/primitives/conversation_loading';
import { EventLine } from '@apps/web/features/phoebe_chat_v3/ui/primitives/event_line';
import { PetalTickLoader } from '@apps/web/features/phoebe_chat_v3/ui/primitives/petal_tick_loader';
import { VIEWER_STOP_REQUEST_COPY } from '@apps/web/features/phoebe_chat_v3/ui/primitives/stop_request_line';
import { QuestionDockContext } from '@apps/web/features/phoebe_chat_v3/ui/question_dock';
import { QueuedMessages } from '@apps/web/features/phoebe_chat_v3/ui/queued_messages';
import { TableSelectionAttachments } from '@apps/web/features/phoebe_chat_v3/ui/table_selection_attachments';
import {
    createTableSelectionStore,
    submitTableSelection,
    TableSelectionContext,
} from '@apps/web/features/phoebe_chat_v3/ui/table_selection_context';
import { useViewerUserId } from '@apps/web/features/phoebe_chat_v3/ui/viewer_user_context';
import {
    type TranscriptRestorationDescriptor,
    VirtualizedTranscript,
    type VirtualizedTranscriptCapture,
} from '@apps/web/features/phoebe_chat_v3/ui/virtualized_transcript';
import { useFormatPersonName } from '@apps/web/hooks/use_person_name_format';
import {
    formatChatTypingLabel,
    usePhoebeChatTyping,
} from '@apps/web/hooks/use_phoebe_chat_typing';
import { useRunUploadAttachments } from '@apps/web/hooks/use_run_upload_attachments';
import { toast } from '@apps/web/lib/app_notifications';
import { EVENTS } from '@apps/web/lib/events';
import { cn } from '@apps/web/lib/utils';

const BASE_REGISTRY = composeDefinitionModules(generatedDefinitionModules);
const SCROLL_POSITION_SETTLEMENT_MS = 150;
const CHAT_MUTATION_DEADLINE_MS = 30_000;
// Keep the latest Stop attempt across remounts. Timeouts release its HTTP
// guard but retain its request ID for deduplication until the response ends.
type StopRequestAttempt = StopRequestBoundary & {
    readonly requestId: string;
    readonly inFlight: boolean;
};
const STOP_REQUEST_ATTEMPTS = new Map<string, StopRequestAttempt>();

export function clearInFlightStopRequests(): void {
    STOP_REQUEST_ATTEMPTS.clear();
}
const TERMINAL_RUN_STATUSES = new Set(['cancelled', 'completed', 'failed']);
// The petal's waiting line while a permission request is open. The composer
// stays usable: sending a message retires the request instead.
const APPROVAL_WAITING_COPY = 'Waiting for your approval…';

function agentTurnHasWork(runState: RunStateRead | null): boolean {
    return (
        runState !== null &&
        (runState.is_generating ||
            runState.has_active_lease ||
            runState.has_pending_work)
    );
}

export type ChatHarnessRestorationDescriptor = TranscriptRestorationDescriptor &
    Readonly<{
        readonly expandedGroupIds: Array<string>;
    }>;

export type ChatHarnessPositionCapture = Readonly<{
    readonly capture: () => ChatHarnessRestorationDescriptor | null;
}>;

type TranscriptPositioningState = Readonly<{
    readonly requestKey: string;
}> &
    (
        | Readonly<{ readonly kind: 'waiting' }>
        | Readonly<{ readonly kind: 'normal' }>
        | Readonly<{
              readonly kind: 'expansion_restored';
              readonly descriptor: TranscriptRestorationDescriptor;
          }>
        | Readonly<{
              readonly kind: 'restoring';
              readonly descriptor: TranscriptRestorationDescriptor;
          }>
    );

export type ChatHarnessProps = Readonly<{
    /** A hidden host keeps drafts mounted but must not stream or mark alerts viewed. */
    readonly visible?: boolean;
    readonly captureRef?: Ref<ChatHarnessPositionCapture>;
    readonly initialDescriptor?: ChatHarnessRestorationDescriptor | null;
    readonly onPositionChange?: (
        descriptor: ChatHarnessRestorationDescriptor,
    ) => void;
    readonly restorationEntryKey?: number | null;
    readonly scope: HarnessScope;
    readonly profile: AgentProfile;
    /** Limits composer mentions to records belonging to one outreach. */
    readonly referenceSearchOutreachId?: string;
    /** Host-owned chrome rendered in the composer's foot, e.g. the
     * approval-mode control. The host owns its state and persistence. */
    readonly composerControls?: ReactNode;
    /** Host-owned reason that temporarily blocks every message entry path. */
    readonly composerDisabledReason?: string | null;
    /** A host-requested draft for the active run's composer. */
    readonly composerPrefill?: ComposerPrefill;
    /** Analytics flow name for the shared voice-input control. */
    readonly voiceInputFlow?: string;
    /** Multiplayer typing presence for shared chats: broadcast the viewer's
     * keystrokes and show teammates typing above the composer. Off for
     * owner-scoped chats, where there is never a second participant. */
    readonly typingPresenceEnabled?: boolean;
    /** The first draft accepted before this run-scoped harness existed. */
    readonly initialMessage?: Readonly<{
        readonly activityEventIds?: ReadonlyArray<string>;
        readonly id: string;
        readonly content: string;
        /** Called once the backend accepts the first draft. */
        readonly onQueued?: () => void;
    }>;
    /** Host-staged context (e.g. calendar-selected shifts): the tray renders
     * inside the composer card, and the references ride the next typed
     * message as reference tokens. `onSent` receives the sent ids once the
     * backend accepts that message, so the host can unstage exactly those. */
    readonly stagedReferences?: HarnessStagedReferences;
}>;

export type HarnessStagedReferences = Readonly<{
    readonly tray: ReactNode;
    readonly references: ReadonlyArray<
        Readonly<{ readonly type: string; readonly id: string }>
    >;
    readonly onSent: (ids: ReadonlyArray<string>) => void;
}>;

function sortedGroupIds(groupIds: ReadonlySet<string>): Array<string> {
    return Array.from(groupIds).sort();
}

function rowContainsCommittedEvent(
    row: ProjectedTranscriptRow,
    committedEventId: string,
): boolean {
    if (
        'eventIds' in row &&
        row.eventIds.some((eventId) => eventId === committedEventId)
    ) {
        return true;
    }
    return row.rowId.endsWith(`committed-event:${committedEventId}`);
}

/** Keep pending interactions on the same run boundary as the transcript. */
type LatestRunProjection = Readonly<{
    readonly committedEventIds: ReadonlySet<string>;
}>;

type LocalStopRequestEvent = StopRequestBoundary &
    Readonly<{
        readonly id: string;
        readonly runKey: string;
        readonly requestId: string;
    }>;

function projectLatestRun(state: TranscriptState): LatestRunProjection {
    const committedEventIds = new Set<string>();
    for (let index = state.committedOrder.length - 1; index >= 0; index -= 1) {
        const eventId = state.committedOrder[index];
        if (eventId === undefined) {
            continue;
        }
        committedEventIds.add(eventId);
        const event = state.committedById.get(eventId);
        if (event === undefined) {
            continue;
        }
        if (
            isTerminalResponseEvent(event.type) ||
            event.type === 'run_started'
        ) {
            break;
        }
    }
    return { committedEventIds };
}

function classifyEventStreamLine(
    value: Readonly<Record<string, unknown>>,
): StreamLineClassification {
    // Framing rejects unknown envelope kinds here. The ingestion core checks
    // cursor semantics, and the selected registry validates payloads before
    // any customer-visible renderer receives them.
    switch (value['type']) {
        case 'snapshot':
        case 'event':
        case 'pending_event':
        case 'pending_event_update':
        case 'run_state':
        case 'heartbeat':
        case 'done':
            return { ok: true, line: value as EventStreamLine };
        default:
            return {
                ok: false,
                message: 'The event stream line has an invalid shape.',
            };
    }
}

function recordTransportIssue(
    profile: AgentProfile,
    failure: EventStreamClientFailure,
): void {
    recordEventChatClientIssue({
        profileId: profile.id,
        rendererVersion: profile.rendererVersion,
        reason: 'protocol_error',
        lineType: failure.kind === 'protocol' ? failure.lineType : null,
        eventType: failure.kind === 'protocol' ? failure.eventType : null,
        count: 1,
    });
}

function projectionTelemetryReason(
    reason: ActivityProjectionIssue['reason'],
): EventChatClientIssue['reason'] {
    switch (reason) {
        case 'known_payload_invalid':
        case 'orphan_slot':
        case 'unknown_event_type':
            return reason;
        case 'missing_customer_narration':
            return 'missing_narration';
        default: {
            const exhaustiveReason: never = reason;
            return exhaustiveReason;
        }
    }
}

function recordProjectionIssue(
    profileId: string,
    rendererVersion: string,
    issue: ActivityProjectionIssue,
): void {
    recordEventChatClientIssue({
        profileId,
        rendererVersion,
        reason: projectionTelemetryReason(issue.reason),
        lineType: null,
        eventType: issue.eventType,
        count: issue.count,
    });
}

type StoppedConnectionReason = Extract<
    ConnectionState,
    { readonly kind: 'stopped' }
>['reason'];

function stoppedConnectionReason(
    failure: EventStreamClientFailure,
): StoppedConnectionReason | null {
    switch (failure.kind) {
        case 'parser':
        case 'protocol':
            return 'protocol_error';
        case 'transport':
            return null;
        case 'http': {
            const reason = failure.reason;
            switch (reason) {
                case 'unauthorized':
                case 'forbidden':
                case 'not_found':
                    return reason;
                case 'unprocessable_entity':
                    return 'invalid_request';
                case 'client_error':
                    return 'protocol_error';
                case 'rate_limited':
                case 'request_timeout':
                case 'server_error':
                    return null;
                default: {
                    const exhaustiveReason: never = reason;
                    return exhaustiveReason;
                }
            }
        }
        default: {
            const exhaustiveFailure: never = failure;
            return exhaustiveFailure;
        }
    }
}

function stoppedConnectionCopy(
    reason: StoppedConnectionReason,
    unavailableBody: string,
): string {
    switch (reason) {
        case 'unauthorized':
            return 'We could not verify your access to this conversation.';
        case 'forbidden':
            return 'You don’t have access to this conversation.';
        case 'not_found':
            return unavailableBody;
        case 'invalid_request':
            return 'This conversation request is invalid.';
        case 'protocol_error':
            return 'This conversation could not be loaded safely.';
        default: {
            const exhaustiveReason: never = reason;
            return exhaustiveReason;
        }
    }
}

function reloadPage(): void {
    window.location.reload();
}

export function ChatHarness({
    captureRef,
    initialDescriptor = null,
    onPositionChange,
    restorationEntryKey = null,
    scope,
    profile,
    referenceSearchOutreachId,
    composerControls,
    composerDisabledReason = null,
    composerPrefill,
    initialMessage,
    stagedReferences,
    voiceInputFlow,
    typingPresenceEnabled = false,
    visible = true,
}: ChatHarnessProps): ReactNode {
    const visibleRef = useRef(visible);
    const hostVisibilityListenerRef = useRef<(() => void) | null>(null);
    useLayoutEffect(() => {
        visibleRef.current = visible;
        hostVisibilityListenerRef.current?.();
    }, [visible]);
    const stableScope = useMemo<HarnessScope>(
        () => ({
            userId: scope.userId,
            organizationId: scope.organizationId,
            ...(scope.organizationName === undefined
                ? {}
                : { organizationName: scope.organizationName }),
            mode: scope.mode,
            runId: scope.runId,
            organizationTimeZone: scope.organizationTimeZone,
        }),
        [
            scope.mode,
            scope.organizationId,
            scope.organizationName,
            scope.organizationTimeZone,
            scope.runId,
            scope.userId,
        ],
    );
    const uploadsEnabled =
        profile.features.composer.kind === 'text' &&
        profile.features.composer.uploads !== false;
    const uploadAttachments = useRunUploadAttachments({
        organizationId: stableScope.organizationId,
        runId: stableScope.runId,
        mode: stableScope.mode,
        enabled: uploadsEnabled,
    });
    const { clearAttachmentsByUploadIds } = uploadAttachments;
    const restorationScopeKey = useMemo(
        () =>
            JSON.stringify([
                stableScope.userId,
                stableScope.organizationId,
                stableScope.mode,
                stableScope.organizationTimeZone,
                profile.id,
                profile.rendererVersion,
                stableScope.runId,
            ]),
        [
            profile.id,
            profile.rendererVersion,
            stableScope.mode,
            stableScope.organizationId,
            stableScope.organizationTimeZone,
            stableScope.runId,
            stableScope.userId,
        ],
    );
    const restorationRequestKey = useMemo(
        () =>
            JSON.stringify([
                restorationScopeKey,
                restorationEntryKey,
                initialDescriptor?.committedEventId ?? null,
                initialDescriptor?.intraRowOffset ?? null,
                [...(initialDescriptor?.expandedGroupIds ?? [])].sort(),
            ]),
        [initialDescriptor, restorationEntryKey, restorationScopeKey],
    );
    const interruptRunKey = useMemo(
        () =>
            JSON.stringify([
                stableScope.userId,
                stableScope.organizationId,
                stableScope.mode,
                stableScope.runId,
            ]),
        [
            stableScope.mode,
            stableScope.organizationId,
            stableScope.runId,
            stableScope.userId,
        ],
    );
    const tableSelectionStore = useMemo(
        () => createTableSelectionStore(),
        // A new chat scope must never inherit another chat's checked rows.
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [interruptRunKey],
    );
    const store = useMemo(() => {
        const foregroundClock = createForegroundClock(
            () => performance.now(),
            visibleRef.current && document.visibilityState === 'visible',
        );
        return createTranscriptStore(
            {
                ...stableScope,
                profileId: profile.id,
                rendererVersion: profile.rendererVersion,
            },
            Date.now(),
            foregroundClock,
        );
    }, [profile.id, profile.rendererVersion, stableScope]);
    const [eventStreamCycleState, setEventStreamCycleState] =
        useState<EventStreamCycleState>('stopped');
    const [
        completedInitialCatchupScopeKey,
        setCompletedInitialCatchupScopeKey,
    ] = useState<string | null>(null);
    const eventStreamCycleStateRef = useRef<EventStreamCycleState>('stopped');
    // Holds the transport React is currently running so a late settle from a
    // send or an answer cannot restart a client that unmount or a scope
    // change already stopped.
    const activeTransportRef = useRef<ReturnType<
        typeof createEventStreamClient
    > | null>(null);
    const visibilityCatchUpRequiredRef = useRef(false);
    const publishEventStreamCycleState = useCallback(
        (nextState: EventStreamCycleState) => {
            eventStreamCycleStateRef.current = nextState;
            if (nextState === 'hidden') {
                visibilityCatchUpRequiredRef.current = true;
            } else if (nextState === 'complete') {
                visibilityCatchUpRequiredRef.current = false;
            }
            if (activeTransportRef.current !== null) {
                setEventStreamCycleState(nextState);
            }
        },
        [],
    );
    const writeTailRef = useRef<Promise<void>>(Promise.resolve());
    const enqueueWrite = useCallback(
        <Result,>(
            write: (signal: AbortSignal) => Promise<Result>,
        ): Promise<Result> => {
            const queuedWrite = writeTailRef.current.then(() => {
                const controller = new AbortController();
                let timer = 0;
                const deadline = new Promise<never>((_resolve, reject) => {
                    timer = window.setTimeout(() => {
                        reject(
                            new DOMException(
                                'Chat request outcome is unknown.',
                                'TimeoutError',
                            ),
                        );
                        controller.abort();
                    }, CHAT_MUTATION_DEADLINE_MS);
                });
                const writeAttempt = Promise.resolve().then(() =>
                    write(controller.signal),
                );
                // Each request gets its own deadline after reaching the front
                // of the queue. A hung predecessor must not consume that time.
                return Promise.race([writeAttempt, deadline]).finally(() =>
                    window.clearTimeout(timer),
                );
            });
            writeTailRef.current = queuedWrite.then(
                () => undefined,
                () => undefined,
            );
            return queuedWrite;
        },
        [],
    );
    const interruptController = useMemo(() => {
        const feature = profile.features.interrupt;
        let ownedAttempt = STOP_REQUEST_ATTEMPTS.get(interruptRunKey);
        return createInterruptController({
            foregroundClock: store.foregroundClock,
            resumePendingStop: ownedAttempt?.inFlight === true,
            onDeadline: () => {
                if (
                    ownedAttempt !== undefined &&
                    STOP_REQUEST_ATTEMPTS.get(interruptRunKey) === ownedAttempt
                ) {
                    // An accepted request may never echo. Re-arm an explicit
                    // retry with its durable ID, without releasing a newer attempt.
                    STOP_REQUEST_ATTEMPTS.set(interruptRunKey, {
                        ...ownedAttempt,
                        inFlight: false,
                    });
                }
            },
            interrupt:
                feature.kind === 'none'
                    ? null
                    : async () => {
                          const previous =
                              STOP_REQUEST_ATTEMPTS.get(interruptRunKey);
                          if (previous?.inFlight) {
                              return { kind: 'ambiguous' };
                          }
                          const snapshot = store.getSnapshot();
                          const boundary: StopRequestAttempt = {
                              ...(previous ??
                                  captureStopRequestBoundary(snapshot)),
                              requestId:
                                  previous?.requestId ??
                                  globalThis.crypto.randomUUID(),
                              inFlight: true,
                          };
                          ownedAttempt = boundary;
                          STOP_REQUEST_ATTEMPTS.set(interruptRunKey, boundary);
                          const release = (definitive = false) => {
                              if (
                                  STOP_REQUEST_ATTEMPTS.get(interruptRunKey) ===
                                  boundary
                              ) {
                                  if (definitive) {
                                      STOP_REQUEST_ATTEMPTS.delete(
                                          interruptRunKey,
                                      );
                                  } else {
                                      // A retry must reuse the durable server
                                      // operation ID even after the HTTP slot expires.
                                      STOP_REQUEST_ATTEMPTS.set(
                                          interruptRunKey,
                                          {
                                              ...boundary,
                                              inFlight: false,
                                          },
                                      );
                                  }
                              }
                          };
                          try {
                              const result = await enqueueWrite((signal) => {
                                  // Stop may have waited behind a send. Once
                                  // its response ends, it must not stop the next one.
                                  if (
                                      responseEndedAfter(
                                          store.getSnapshot(),
                                          boundary.afterEventOffset,
                                      )
                                  ) {
                                      return Promise.resolve({
                                          kind: 'accepted' as const,
                                          interrupted: false,
                                      });
                                  }
                                  return feature.interrupt(
                                      stableScope,
                                      signal,
                                      boundary.requestId,
                                  );
                              });
                              if (
                                  result.kind !== 'accepted' ||
                                  !result.interrupted
                              ) {
                                  release(result.kind !== 'ambiguous');
                              }
                              return result;
                          } catch (error: unknown) {
                              release();
                              throw error;
                          }
                      },
        });
    }, [
        enqueueWrite,
        interruptRunKey,
        profile.features.interrupt,
        stableScope,
        store,
    ]);
    const transport = useMemo(() => {
        // The transport acknowledges data lines before it sees `done`. Reduce
        // against a private draft, then publish the complete finite cycle once
        // so React never participates in protocol acknowledgement.
        let draftState = store.getSnapshot();
        let cycleLines: Array<EventStreamLine> = [];
        let doneLine: DoneLine | null = null;
        let cycleNumber = 0;
        let initialCatchupComplete = false;
        return createEventStreamClient({
            scope: stableScope,
            classifyLine(value) {
                const result = classifyEventStreamLine(value);
                if (result.ok && result.line.type === 'done') {
                    doneLine = result.line;
                }
                return result;
            },
            openEventStream(request) {
                cycleNumber += 1;
                store.dispatchLocal({
                    type: 'cycle_started',
                    cycleId: `transport-cycle-${cycleNumber}`,
                    nowMs: Date.now(),
                });
                draftState = store.getSnapshot();
                cycleLines = [];
                doneLine = null;
                return profile.transport.openEventStream(request);
            },
            ingestLine(line) {
                const previousRunState = draftState.runState;
                const result = applyStreamLine(draftState, line, Date.now());
                if (result.ok) {
                    draftState = result.state;
                    cycleLines.push(line);
                    if (store.getSnapshot().connection.kind === 'stopped') {
                        const nowMs = Date.now();
                        store.dispatchLocal({
                            type: 'connection_changed',
                            connection: {
                                kind: 'connected',
                                sinceMs: nowMs,
                                lastLineAtMs: nowMs,
                            },
                        });
                    }
                    return {
                        ok: true,
                        acknowledgedCursor: result.acknowledgedCursor,
                        runStateChanged:
                            line.type === 'run_state' &&
                            result.state.runState !== previousRunState,
                    };
                }
                return { ok: false, problem: result.problem };
            },
            publishCycle() {
                const completedLines =
                    doneLine === null ? cycleLines : [...cycleLines, doneLine];
                store.ingestResponse(completedLines, Date.now());
            },
            onCycleComplete() {
                if (!initialCatchupComplete) {
                    initialCatchupComplete = true;
                    setCompletedInitialCatchupScopeKey(restorationScopeKey);
                }
            },
            recordIssue(failure) {
                recordTransportIssue(profile, failure);
            },
            handoffFailure(failure) {
                // The first protocol failure also hands off while its replay is
                // live. Only a terminal client stop may replace the connection.
                if (eventStreamCycleStateRef.current !== 'stopped') {
                    return;
                }
                const reason = stoppedConnectionReason(failure);
                if (reason === null) {
                    return;
                }
                store.dispatchLocal({
                    type: 'connection_changed',
                    connection: {
                        kind: 'stopped',
                        sinceMs: Date.now(),
                        reason,
                    },
                });
            },
            schedule(callback, delayMs) {
                const handle = window.setTimeout(callback, delayMs);
                return () => window.clearTimeout(handle);
            },
            queueMicrotask(callback) {
                let cancelled = false;
                globalThis.queueMicrotask(() => {
                    if (!cancelled) {
                        callback();
                    }
                });
                return () => {
                    cancelled = true;
                };
            },
            // Jitter prevents every browser from retrying together after an
            // organization-wide outage and stampeding the API.
            jitterFactor: () => 1 + (Math.random() - 0.5) * 0.4,
            getActivityState() {
                const evidence = store.getThinkingEvidence();
                switch (evidence.kind) {
                    case 'thinking':
                    case 'queued':
                    case 'background_work':
                        return 'active';
                    case 'waiting':
                        return 'waiting';
                    case 'terminal':
                    case 'interrupted':
                        return 'terminal';
                    case 'idle':
                    case 'stale':
                        return 'idle';
                    default: {
                        const exhaustiveEvidence: never = evidence;
                        return exhaustiveEvidence;
                    }
                }
            },
            foregroundClock: store.foregroundClock,
            getVisible: () =>
                visibleRef.current && document.visibilityState === 'visible',
            subscribeVisibility(listener) {
                hostVisibilityListenerRef.current = listener;
                document.addEventListener('visibilitychange', listener);
                return () => {
                    hostVisibilityListenerRef.current = null;
                    document.removeEventListener('visibilitychange', listener);
                };
            },
            getOnline: () => window.navigator.onLine,
            subscribeOnline(listener) {
                window.addEventListener('online', listener);
                window.addEventListener('offline', listener);
                return () => {
                    window.removeEventListener('online', listener);
                    window.removeEventListener('offline', listener);
                };
            },
            setCycleState: publishEventStreamCycleState,
        });
    }, [
        profile,
        publishEventStreamCycleState,
        restorationScopeKey,
        stableScope,
        store,
    ]);
    const state = useSyncExternalStore(
        store.subscribe,
        store.getSnapshot,
        store.getSnapshot,
    );
    const answerTail = useMemo(() => projectAnswerTail(state), [state]);
    const interruptState = useSyncExternalStore(
        interruptController.subscribe,
        interruptController.getSnapshot,
        interruptController.getSnapshot,
    );
    const localStopRequestSequenceRef = useRef(0);
    const [localStopRequestEvents, setLocalStopRequestEvents] = useState<
        ReadonlyArray<LocalStopRequestEvent>
    >([]);
    const [foregroundTimeRevision, setForegroundTimeRevision] = useState(0);
    const reduceMotion = useReducedMotion();
    const thinkingEvidence = store.getThinkingEvidence();
    const latestRun = useMemo(() => projectLatestRun(state), [state]);
    const inFlightStopRequest = STOP_REQUEST_ATTEMPTS.get(interruptRunKey);
    const stopBoundary =
        localStopRequestEvents
            .slice()
            .reverse()
            .find((event) => event.runKey === interruptRunKey) ??
        inFlightStopRequest;
    const stopProjections = useMemo(() => {
        const boundaries = new Map<string, StopRequestBoundary>();
        for (const event of localStopRequestEvents) {
            if (event.runKey === interruptRunKey) {
                boundaries.set(event.requestId, event);
            }
        }
        if (inFlightStopRequest !== undefined) {
            boundaries.set(inFlightStopRequest.requestId, inFlightStopRequest);
        }
        return projectStopRequests(state, boundaries);
    }, [inFlightStopRequest, interruptRunKey, localStopRequestEvents, state]);
    const stopProjection =
        (stopBoundary === undefined
            ? undefined
            : stopProjections.byRequestId.get(stopBoundary.requestId)) ??
        stopProjections.current;
    const stopRequestAcknowledged =
        inFlightStopRequest !== undefined &&
        stopProjections.byRequestId.get(inFlightStopRequest.requestId)
            ?.acknowledged === true;
    const stopRequestUnacknowledged =
        inFlightStopRequest !== undefined &&
        inFlightStopRequest.inFlight &&
        !stopRequestAcknowledged;
    // Activity and Stop-dependent surfaces share the model's response phase
    // and Stop projection. A controller that timed out or was rejected is
    // deliberately not a stop signal: those stops may be retried, so their
    // recovery copy and a re-armed Stop must win over stickiness. The idle
    // case covers a remount whose in-flight stop has not echoed yet.
    const {
        phase: responsePhase,
        runEnded: responseRunEnded,
        stoppedResponseEnded,
    } = deriveResponsePhase(thinkingEvidence, state, {
        stopRequest: stopProjection,
        localStopRequested:
            interruptState.kind === 'pending' ||
            interruptState.kind === 'accepted' ||
            (interruptState.kind === 'idle' && stopRequestUnacknowledged),
        stopRequestInFlight:
            (interruptState.kind === 'pending' ||
                interruptState.kind === 'idle') &&
            stopRequestUnacknowledged,
    });
    const responsePhaseLive = responsePhaseIsLive(responsePhase);
    const connectionFailureCopy =
        state.connection.kind === 'stopped'
            ? stoppedConnectionCopy(
                  state.connection.reason,
                  profile.copy.unavailableBody,
              )
            : null;
    const [expandedGroupIds, setExpandedGroupIds] = useState<
        ReadonlySet<string>
    >(new Set());
    const expandedGroupIdsRef = useRef<ReadonlySet<string>>(expandedGroupIds);
    expandedGroupIdsRef.current = expandedGroupIds;
    const transcriptCaptureRef = useRef<VirtualizedTranscriptCapture | null>(
        null,
    );
    const [positioningState, setPositioningState] =
        useState<TranscriptPositioningState>({
            kind: 'waiting',
            requestKey: restorationRequestKey,
        });
    const positioningStateRef = useRef(positioningState);
    positioningStateRef.current = positioningState;
    const positioningReady =
        positioningState.requestKey === restorationRequestKey &&
        positioningState.kind === 'normal';
    const activeTranscriptDescriptor =
        positioningState.requestKey === restorationRequestKey &&
        positioningState.kind === 'restoring'
            ? positioningState.descriptor
            : null;
    const positioningReadyRef = useRef(positioningReady);
    positioningReadyRef.current = positioningReady;
    const pendingPositionChangeRef = useRef<number | null>(null);
    const cancelPendingPositionChange = useCallback(() => {
        if (pendingPositionChangeRef.current === null) {
            return;
        }
        window.clearTimeout(pendingPositionChangeRef.current);
        pendingPositionChangeRef.current = null;
    }, []);
    const publishPosition = useCallback(
        (
            descriptor: TranscriptRestorationDescriptor,
            groupIds: ReadonlySet<string>,
        ) => {
            if (!positioningReadyRef.current) {
                return;
            }
            onPositionChange?.({
                ...descriptor,
                expandedGroupIds: sortedGroupIds(groupIds),
            });
        },
        [onPositionChange],
    );
    const settlePosition = useCallback(
        (
            descriptor: TranscriptRestorationDescriptor,
            groupIds: ReadonlySet<string>,
        ) => {
            cancelPendingPositionChange();
            pendingPositionChangeRef.current = window.setTimeout(() => {
                pendingPositionChangeRef.current = null;
                publishPosition(descriptor, groupIds);
            }, SCROLL_POSITION_SETTLEMENT_MS);
        },
        [cancelPendingPositionChange, publishPosition],
    );
    const capturePosition =
        useCallback((): ChatHarnessRestorationDescriptor | null => {
            cancelPendingPositionChange();
            if (!positioningReadyRef.current) {
                return null;
            }
            const descriptor = transcriptCaptureRef.current?.capture() ?? null;
            if (descriptor === null) {
                return null;
            }
            return {
                ...descriptor,
                expandedGroupIds: sortedGroupIds(expandedGroupIdsRef.current),
            };
        }, [cancelPendingPositionChange]);
    useImperativeHandle(captureRef, () => ({ capture: capturePosition }), [
        capturePosition,
    ]);
    const handleTranscriptPositionChange = useCallback(
        (descriptor: TranscriptRestorationDescriptor) => {
            const currentPositioningState = positioningStateRef.current;
            if (currentPositioningState.requestKey !== restorationRequestKey) {
                return;
            }
            if (currentPositioningState.kind === 'restoring') {
                if (
                    descriptor.committedEventId !==
                        currentPositioningState.descriptor.committedEventId ||
                    descriptor.intraRowOffset !==
                        currentPositioningState.descriptor.intraRowOffset
                ) {
                    return;
                }
                const restoredPositioningState: TranscriptPositioningState = {
                    kind: 'normal',
                    requestKey: restorationRequestKey,
                };
                positioningStateRef.current = restoredPositioningState;
                positioningReadyRef.current = true;
                setPositioningState(restoredPositioningState);
                publishPosition(descriptor, expandedGroupIdsRef.current);
                return;
            }
            settlePosition(descriptor, expandedGroupIdsRef.current);
        },
        [publishPosition, restorationRequestKey, settlePosition],
    );
    useEffect(
        () => cancelPendingPositionChange,
        [cancelPendingPositionChange, restorationRequestKey],
    );
    const registry = useMemo(
        () =>
            composeRendererRegistry<RendererRegistryShape>(
                BASE_REGISTRY,
                profile.rendererOverlay,
            ),
        [profile.rendererOverlay],
    );
    const inFlightInteractionCallIdsRef = useRef(new Set<string>());
    const lastOptimisticCreatedAtMsRef = useRef(0);
    // The original request per operation, kept so a failed send can be
    // retried verbatim. The route dedupes on the operation id, so a retry
    // can never deliver twice. Same lifetime as the store's optimistic map.
    const sentRequestsByOperationIdRef = useRef(
        new Map<string, MessageCreateRequest>(),
    );
    // Staged host references are frozen per operation: reserved while their
    // send is in flight so a concurrent message cannot re-attach them, and
    // settled from the shared send path so a retry that succeeds still
    // unstages them (first attempts and retryFailedSends both land there).
    const stagedReferencesRef = useRef(stagedReferences);
    stagedReferencesRef.current = stagedReferences;
    const stagedIdsByOperationIdRef = useRef(
        new Map<string, ReadonlyArray<string>>(),
    );
    const inFlightStagedIdsRef = useRef(new Set<string>());
    const settleStagedReferences = useCallback(
        (operationId: string, posted: boolean) => {
            const stagedIds =
                stagedIdsByOperationIdRef.current.get(operationId);
            if (stagedIds === undefined) {
                return;
            }
            for (const id of stagedIds) {
                inFlightStagedIdsRef.current.delete(id);
            }
            // On failure the ids stay mapped and staged: the failed message
            // keeps its tokens for the retry, and that retry's success
            // unstages them here. `onSent` removal is idempotent, so an id
            // that also rode a newer message cannot be double-removed.
            if (posted) {
                stagedIdsByOperationIdRef.current.delete(operationId);
                stagedReferencesRef.current?.onSent(stagedIds);
            }
        },
        [],
    );
    const refreshAuthoritativeState = useCallback(() => {
        if (activeTransportRef.current !== transport) {
            return;
        }
        transport.stop();
        transport.start();
    }, [transport]);
    const performSend = useCallback(
        async (
            send: MessageSendPort,
            operationId: string,
            request: MessageCreateRequest,
        ): Promise<boolean> => {
            try {
                await enqueueWrite((signal) =>
                    send(stableScope, request, signal),
                );
                // Only an accepted message starts new work. Failed sends must
                // preserve an already-inactive response's settled Stop state.
                interruptController.reset();
                store.dispatchLocal({
                    type: 'optimistic_send_posted',
                    operationId,
                });
                settleStagedReferences(operationId, true);
                return true;
            } catch {
                store.dispatchLocal({
                    type: 'optimistic_send_failed',
                    operationId,
                    errorKind: 'unknown',
                });
                settleStagedReferences(operationId, false);
                return false;
            } finally {
                refreshAuthoritativeState();
            }
        },
        [
            enqueueWrite,
            interruptController,
            refreshAuthoritativeState,
            settleStagedReferences,
            stableScope,
            store,
        ],
    );
    const retryFailedSends = useCallback(() => {
        const feature = profile.features.composer;
        if (feature.kind !== 'text') {
            return;
        }
        for (const message of store
            .getSnapshot()
            .optimisticByOperationId.values()) {
            if (
                message.status !== 'failed' ||
                message.matchedPendingId !== null ||
                message.matchedCommittedId !== null
            ) {
                continue;
            }
            const request = sentRequestsByOperationIdRef.current.get(
                message.operationId,
            );
            if (request === undefined) {
                continue;
            }
            store.dispatchLocal({
                type: 'optimistic_send_retried',
                operationId: message.operationId,
            });
            void performSend(feature.send, message.operationId, request).then(
                (posted) => {
                    // The composer clears delivered attachments itself on a
                    // first-try success; a retry succeeds outside that path,
                    // so clear here or the same uploads ride the next message.
                    if (posted && request.upload_ids !== undefined) {
                        clearAttachmentsByUploadIds(request.upload_ids);
                    }
                },
            );
        }
    }, [
        clearAttachmentsByUploadIds,
        performSend,
        profile.features.composer,
        store,
    ]);
    // Cards still waiting on the user when a message is sent. sendMessage
    // reads the ref so its identity does not change with every projection.
    const pendingInteractionCommandIdsRef = useRef<ReadonlyArray<string>>([]);
    const submitSelectedMessage = useCallback(
        (
            content: string,
            send: Parameters<typeof submitTableSelection>[0]['send'],
            restoreOnFailure = false,
        ) =>
            submitTableSelection({
                content,
                store: tableSelectionStore,
                send,
                restoreOnFailure,
                onTooLong: () =>
                    toast.error(
                        'Too many rows for one message. Pick fewer rows.',
                        { persistNotification: false },
                    ),
            }),
        [tableSelectionStore],
    );
    const sendMessage = useCallback(
        (
            content: string,
            activityEventIds: ReadonlyArray<string>,
            uploadIds: ReadonlyArray<string>,
            stagedReferenceIds: ReadonlyArray<string> = [],
        ): Promise<boolean> | null => {
            const feature = profile.features.composer;
            if (feature.kind !== 'text') {
                return null;
            }

            return submitSelectedMessage(content, (message) => {
                const operationId = globalThis.crypto.randomUUID();
                const request: MessageCreateRequest = {
                    ...(activityEventIds.length === 0
                        ? {}
                        : {
                              activity_event_ids: Array.from(activityEventIds),
                          }),
                    ...message,
                    client_operation_id: operationId,
                    ...(uploadIds.length === 0
                        ? {}
                        : { upload_ids: Array.from(uploadIds) }),
                };
                // Same-millisecond optimistic sends stay in click order.
                const createdAtMs = Math.max(
                    Date.now(),
                    lastOptimisticCreatedAtMsRef.current + 1,
                );
                lastOptimisticCreatedAtMsRef.current = createdAtMs;
                // Decided once, here: the queue chrome is for follow-ups sent while
                // Phoebe has work in hand. Deciding per render instead would bounce
                // the message into the queue for the instant the turn starts, and
                // the thinking presentation is wider than real work (a fresh run
                // reads as thinking before it has anything to do).
                const sentWhileBusy = agentTurnHasWork(
                    store.getSnapshot().runState,
                );

                sentRequestsByOperationIdRef.current.set(operationId, request);
                if (stagedReferenceIds.length > 0) {
                    stagedIdsByOperationIdRef.current.set(
                        operationId,
                        stagedReferenceIds,
                    );
                    for (const id of stagedReferenceIds) {
                        inFlightStagedIdsRef.current.add(id);
                    }
                }
                store.dispatchLocal({
                    type: 'optimistic_send_enqueued',
                    operationId,
                    request,
                    createdAtMs,
                    sentWhileBusy,
                    supersededCommandIds:
                        pendingInteractionCommandIdsRef.current,
                });
                return performSend(feature.send, operationId, request);
            });
        },
        [performSend, profile.features.composer, store, submitSelectedMessage],
    );
    const requestGroupToggle = useCallback(
        (groupId: string) => {
            cancelPendingPositionChange();
            const next = new Set(expandedGroupIdsRef.current);
            if (next.has(groupId)) {
                next.delete(groupId);
            } else {
                next.add(groupId);
            }
            expandedGroupIdsRef.current = next;
            setExpandedGroupIds(next);
            const descriptor = transcriptCaptureRef.current?.capture() ?? null;
            if (descriptor !== null) {
                publishPosition(descriptor, next);
            }
        },
        [cancelPendingPositionChange, publishPosition],
    );
    const requestQuestionSubmit = useCallback<
        SafeRenderContext['requestQuestionSubmit']
    >(
        async (callId, request) => {
            const feature = profile.features.questions;
            const inFlight = inFlightInteractionCallIdsRef.current;
            if (
                feature.kind === 'none' ||
                request.callId !== callId ||
                inFlight.has(callId)
            ) {
                return 'ignored';
            }

            inFlight.add(callId);
            try {
                const result = await enqueueWrite((signal) =>
                    feature.answer(stableScope, request, signal),
                );
                switch (result.kind) {
                    case 'accepted':
                    case 'already_resolved':
                        // Between long-polls the stream sleeps at its waiting
                        // cadence; without a wake the settled answer shows as
                        // queued until the next poll happens to run.
                        refreshAuthoritativeState();
                        return result.kind;
                    case 'definitively_rejected':
                        toast.error(
                            'Phoebe could not record that answer. Try again.',
                            { persistNotification: false },
                        );
                        return 'rejected';
                    case 'ambiguous':
                        toast.error(
                            'Phoebe could not record that answer. Try again.',
                            { persistNotification: false },
                        );
                        return 'ambiguous';
                    default: {
                        const exhaustiveResult: never = result;
                        return exhaustiveResult;
                    }
                }
            } catch {
                toast.error('Phoebe could not record that answer. Try again.', {
                    persistNotification: false,
                });
                return 'ambiguous';
            } finally {
                inFlight.delete(callId);
            }
        },
        [
            enqueueWrite,
            profile.features.questions,
            refreshAuthoritativeState,
            stableScope,
        ],
    );
    const requestToolDecision = useCallback<
        SafeRenderContext['requestToolDecision']
    >(
        async (request) => {
            const feature = profile.features.toolApprovals;
            const callIds =
                request.targetKind === 'single'
                    ? [request.callId]
                    : request.callIds;
            const inFlight = inFlightInteractionCallIdsRef.current;

            if (
                feature.kind === 'none' ||
                callIds.length === 0 ||
                callIds.some((callId) => inFlight.has(callId))
            ) {
                return 'ignored';
            }

            for (const callId of callIds) {
                inFlight.add(callId);
            }
            try {
                const result = await enqueueWrite((signal) =>
                    feature.decide(stableScope, request, signal),
                );
                switch (result.kind) {
                    case 'accepted':
                    case 'already_resolved':
                        refreshAuthoritativeState();
                        return result.kind;
                    case 'definitively_rejected':
                        toast.error(
                            'Phoebe could not record that decision. Try again.',
                            { persistNotification: false },
                        );
                        return 'rejected';
                    case 'ambiguous':
                        toast.error(
                            'Phoebe could not record that decision. Try again.',
                            { persistNotification: false },
                        );
                        return 'ambiguous';
                    default: {
                        const exhaustiveResult: never = result;
                        return exhaustiveResult;
                    }
                }
            } catch {
                toast.error(
                    'Phoebe could not record that decision. Try again.',
                    { persistNotification: false },
                );
                return 'ambiguous';
            } finally {
                for (const callId of callIds) {
                    inFlight.delete(callId);
                }
            }
        },
        [
            enqueueWrite,
            profile.features.toolApprovals,
            refreshAuthoritativeState,
            stableScope,
        ],
    );
    // A table action sends on the reader's behalf through the composer's own
    // path, so it shows, retries, and fails like typed text. The one check
    // the composer would have made first is the length; sendMessage checks it.
    const requestSendMessage = useCallback<
        SafeRenderContext['requestSendMessage']
    >(
        (content) => {
            void sendMessage(content, [], []);
        },
        [sendMessage],
    );
    // Cards hand off with a built href. The harness can also mount without
    // a router (unit tests), so a missing one just drops the request.
    const router = useRouter({ warn: false });
    const requestNavigate = useCallback<SafeRenderContext['requestNavigate']>(
        (href) => {
            void router?.navigate({ href });
        },
        [router],
    );
    const { formatName: formatPersonName } = useFormatPersonName();
    const renderNowMs = Date.now();
    const renderContext = useMemo<SafeRenderContext>(
        () => ({
            scope: stableScope,
            nowMs: renderNowMs,
            organizationTimeZone: stableScope.organizationTimeZone,
            formatPersonName,
            isExpanded: false,
            tableOutreaches: state.runState?.resources?.flatMap((resource) =>
                resource.resource_type === 'outreach' &&
                resource.role === 'related' &&
                resource.created_by_event_id !== null
                    ? [
                          {
                              outreachId: resource.resource_id,
                              sourceEventId: resource.created_by_event_id,
                          },
                      ]
                    : [],
            ),
            requestGroupToggle,
            requestRetry: refreshAuthoritativeState,
            requestQuestionSubmit,
            requestToolDecision,
            requestSendMessage,
            requestNavigate,
        }),
        [
            formatPersonName,
            refreshAuthoritativeState,
            requestGroupToggle,
            requestQuestionSubmit,
            requestToolDecision,
            requestSendMessage,
            requestNavigate,
            renderNowMs,
            stableScope,
            state.runState?.resources,
        ],
    );
    const activityProjection = useMemo(
        () =>
            projectActivityOccurrences(
                state,
                renderContext,
                registry as ActivityProjectionRegistry,
            ),
        [registry, renderContext, state],
    );
    const pendingInteractionCommandIds = useMemo(() => {
        const commandIds: Array<string> = [];
        for (const compoundState of activityProjection.compoundStateByOwnerOccurrenceId.values()) {
            if (compoundState.approval?.kind === 'approval_required') {
                commandIds.push(compoundState.approval.commandId);
            }
            if (compoundState.question?.kind === 'question_required') {
                commandIds.push(compoundState.question.commandId);
            }
        }
        return commandIds;
    }, [activityProjection.compoundStateByOwnerOccurrenceId]);
    useEffect(() => {
        pendingInteractionCommandIdsRef.current = pendingInteractionCommandIds;
    }, [pendingInteractionCommandIds]);
    // While a superseding send is alive the cards are already drawn expired,
    // so the petal must not keep saying Phoebe waits on them.
    const interactionLocallySuperseded = useMemo(
        () =>
            Array.from(state.optimisticByOperationId.values()).some(
                (message) =>
                    message.status !== 'failed' &&
                    message.matchedCommittedId === null &&
                    message.supersededCommandIds.length > 0,
            ),
        [state.optimisticByOperationId],
    );
    useEffect(() => {
        for (const issue of activityProjection.issues) {
            recordProjectionIssue(profile.id, profile.rendererVersion, issue);
        }
    }, [activityProjection.issues, profile.id, profile.rendererVersion]);
    const interactionOwnerIds = useMemo(() => {
        const ownerIds = new Set<string>();
        for (const [
            occurrenceId,
            compoundState,
        ] of activityProjection.compoundStateByOwnerOccurrenceId) {
            if (
                compoundState.question !== null ||
                compoundState.approval !== null
            ) {
                ownerIds.add(occurrenceId);
            }
        }
        return ownerIds;
    }, [activityProjection.compoundStateByOwnerOccurrenceId]);
    const posthog = usePostHog();
    const forcedVisibleOwnerIds = useMemo(() => {
        const ownerIds = new Set<string>();
        for (const [
            occurrenceId,
            compoundState,
        ] of activityProjection.compoundStateByOwnerOccurrenceId) {
            if (
                compoundState.question !== null ||
                compoundState.approval !== null ||
                compoundState.result?.presentation !== undefined
            ) {
                ownerIds.add(occurrenceId);
            }
        }
        for (const occurrence of activityProjection.occurrences) {
            if (
                occurrence.kind === 'activity_occurrence' &&
                occurrence.group.carriesCard === true
            ) {
                ownerIds.add(occurrence.occurrenceId);
            }
        }
        return ownerIds;
    }, [
        activityProjection.compoundStateByOwnerOccurrenceId,
        activityProjection.occurrences,
    ]);
    const presentationOwnerIds = useMemo(() => {
        const ownerIds = new Set<string>();
        for (const [
            occurrenceId,
            compoundState,
        ] of activityProjection.compoundStateByOwnerOccurrenceId) {
            // An approval is already visible at its tool's transcript row.
            // Its result replaces that card in place, including while the
            // agent is still preparing its next written answer.
            if (
                compoundState.result?.presentation !== undefined &&
                compoundState.approval === null
            ) {
                ownerIds.add(occurrenceId);
            }
        }
        return ownerIds;
    }, [activityProjection.compoundStateByOwnerOccurrenceId]);
    const activeWorkPartition = useMemo(
        () =>
            partitionActiveWorkOccurrences(
                activityProjection.occurrences,
                interactionOwnerIds,
            ),
        [activityProjection.occurrences, interactionOwnerIds],
    );
    const activeWork = useMemo(() => {
        const items = [...activeWorkPartition.activeWork];
        const runState = state.runState;
        if (runState !== null && !TERMINAL_RUN_STATUSES.has(runState.status)) {
            for (const watch of runState.watches ?? []) {
                if (watch.status !== 'active' && watch.status !== 'suspended') {
                    continue;
                }
                items.push({
                    kind: 'watch' as const,
                    id: `watch:${watch.id}`,
                    reason: watch.reason,
                });
            }
        }
        return items;
    }, [activeWorkPartition.activeWork, state.runState]);
    // While Phoebe waits on the user (a pending approval or question) the run
    // is parked: nothing is thinking, so nothing offers a stop. A lingering
    // lease while parked still hides the stop, but active generation with a
    // card pending (the run loop deliberately keeps working on other ready
    // work after a wake) keeps it. A message sent meanwhile retires the card
    // locally, which ends the wait. Watches and check-ins remain stoppable
    // work of their own.
    const waitingOnUser =
        !interactionLocallySuperseded &&
        (responsePhase.kind === 'waiting' ||
            ((state.runState?.has_pending_approval === true ||
                state.runState?.has_pending_user_input === true) &&
                state.runState?.is_generating !== true));
    const responseStopEligible =
        !waitingOnUser &&
        !stopProjection.recorded &&
        (responsePhase.kind === 'working' ||
            responsePhase.kind === 'sending' ||
            (activeWork.length > 0 && state.connection.kind !== 'stopped'));
    // An idle response with no remaining stoppable work also settles a
    // failed/ambiguous Stop. Chat runs can park without a terminal event;
    // retaining recovery chrome in that state strands a useless Stop button.
    const interruptResolvedAuthoritatively =
        !responsePhaseLive &&
        (responseRunEnded ||
            (responsePhase.kind === 'idle' &&
                !responseStopEligible &&
                interruptState.kind !== 'idle'));
    // Local stop rows belong to the run they were clicked in and stay until
    // the server echoes a newer stop event.
    const unacknowledgedLocalStopEvents = useMemo(
        () =>
            localStopRequestEvents.filter(
                (event) =>
                    event.runKey === interruptRunKey &&
                    stopProjections.byRequestId.get(event.requestId)?.echoed !==
                        true,
            ),
        [interruptRunKey, localStopRequestEvents, stopProjections],
    );
    const stopRequestCurrentlyPending = responsePhase.kind === 'stopping';
    const presentedInterruptState = interruptResolvedAuthoritatively
        ? ({ kind: 'authoritatively_resolved' } as const)
        : stopRequestCurrentlyPending
          ? ({ kind: 'pending' } as const)
          : interruptState;
    const requestStopResponse = useCallback(() => {
        if (
            profile.features.interrupt.kind !== 'supported' ||
            responsePhase.kind === 'needs_attention' ||
            !responseStopEligible ||
            stopRequestCurrentlyPending
        ) {
            return;
        }
        const previousAttempt = STOP_REQUEST_ATTEMPTS.get(interruptRunKey);
        interruptController.requestStop();
        const attempt = STOP_REQUEST_ATTEMPTS.get(interruptRunKey);
        if (attempt?.inFlight !== true || attempt === previousAttempt) {
            return;
        }
        const id = String(localStopRequestSequenceRef.current);
        localStopRequestSequenceRef.current += 1;
        setLocalStopRequestEvents((events) =>
            events.some(
                (event) =>
                    event.runKey === interruptRunKey &&
                    event.requestId === attempt.requestId,
            )
                ? events
                : [...events, { ...attempt, id, runKey: interruptRunKey }],
        );
    }, [
        interruptController,
        interruptRunKey,
        profile.features.interrupt.kind,
        responsePhase.kind,
        responseStopEligible,
        stopRequestCurrentlyPending,
    ]);
    // A dismissed docked question collapses back to its transcript position;
    // only a different pending question re-opens the panel.
    const [dismissedQuestionOwnerId, setDismissedQuestionOwnerId] = useState<
        string | null
    >(null);
    // One draft shared by the normal and docked composer instances, so the
    // typed text — and a late failure restore — survives docking, dismissal,
    // and question resolution.
    const composerDraftState = useState('');
    // Multiplayer typing presence. `enabled` gates outbound pings only; the
    // listener always runs, and for an owner-scoped chat no teammate ever
    // publishes, so the indicator simply never appears there. The viewer id
    // filters own pings out of the display.
    const viewerUserId = useViewerUserId();
    const { typingUsers: chatTypingUsers, notifyTyping: notifyChatTyping } =
        usePhoebeChatTyping({
            organizationId: stableScope.organizationId,
            runId: stableScope.runId,
            currentUserId: viewerUserId,
            enabled: typingPresenceEnabled,
        });
    const pendingQuestionOwners = useMemo(
        () =>
            activeWorkPartition.transcriptOccurrences.filter((occurrence) => {
                const compoundState =
                    activityProjection.compoundStateByOwnerOccurrenceId.get(
                        occurrence.occurrenceId,
                    );
                return (
                    compoundState?.question?.kind === 'question_required' &&
                    occurrence.eventIds.some((eventId) =>
                        latestRun.committedEventIds.has(eventId),
                    )
                );
            }),
        [
            activeWorkPartition.transcriptOccurrences,
            activityProjection.compoundStateByOwnerOccurrenceId,
            latestRun.committedEventIds,
        ],
    );
    const activeQuestionComposer = useMemo(() => {
        if (interruptResolvedAuthoritatively) {
            return null;
        }
        const owners = pendingQuestionOwners;
        // A run promises one pending question. If an invalid stream ever
        // presents more, leave each control in chronological transcript order
        // instead of arbitrarily moving one into the composer.
        if (owners.length !== 1) {
            return null;
        }
        const owner = owners[0];
        if (
            owner === undefined ||
            (owner.kind !== 'activity_occurrence' &&
                owner.kind !== 'ordinary_occurrence') ||
            owner.occurrenceId === dismissedQuestionOwnerId
        ) {
            return null;
        }
        return { ownerId: owner.occurrenceId, node: owner.node };
    }, [
        dismissedQuestionOwnerId,
        interruptResolvedAuthoritatively,
        pendingQuestionOwners,
    ]);
    // Simultaneous pending approvals stack into an accordion: one expanded
    // card, the rest collapsed rows (PHO-17295). The reader's explicit pick
    // holds only while that approval is still pending; otherwise the topmost
    // pending approval is the active one, which also advances the stack as
    // decisions resolve.
    const pendingApprovalOwnerIds = useMemo(
        () =>
            activeWorkPartition.transcriptOccurrences
                .filter(
                    (occurrence) =>
                        activityProjection.compoundStateByOwnerOccurrenceId.get(
                            occurrence.occurrenceId,
                        )?.approval?.kind === 'approval_required',
                )
                .map((occurrence) => occurrence.occurrenceId),
        [
            activeWorkPartition.transcriptOccurrences,
            activityProjection.compoundStateByOwnerOccurrenceId,
        ],
    );
    const [preferredApprovalOwnerId, setPreferredApprovalOwnerId] = useState<
        string | null
    >(null);
    const approvalStack = useMemo<ApprovalStack>(
        () => ({
            pendingOwnerIds: pendingApprovalOwnerIds,
            activeOwnerId:
                preferredApprovalOwnerId !== null &&
                pendingApprovalOwnerIds.includes(preferredApprovalOwnerId)
                    ? preferredApprovalOwnerId
                    : (pendingApprovalOwnerIds[0] ?? null),
            activate: setPreferredApprovalOwnerId,
        }),
        [pendingApprovalOwnerIds, preferredApprovalOwnerId],
    );
    const queuedMessages = useMemo(
        () => projectQueuedUserMessages(state),
        [state],
    );
    const queuedMessageOccurrenceIds = useMemo(
        () => new Set(queuedMessages.map((message) => message.occurrenceId)),
        [queuedMessages],
    );
    // The active question moves into the composer while its transcript
    // position remains reserved by an invisible boundary. Queued messages
    // live in the stack directly above the composer until the backend commits
    // them, at which point their stable occurrence returns to normal history.
    // Whether Phoebe is still working on this conversation: any live phase,
    // including a send of ours the stream has not echoed yet and a stop that
    // has not reached its terminal event.
    const runActive = responsePhaseLive;
    const phoebeBusy = runActive && !waitingOnUser;
    const answerInCurrentTurn =
        !answerTail.runStartedAfterAnswer && !answerTail.userMessageAfterAnswer;
    const latestAssistantAnswer = useMemo<LatestAssistantAnswer | null>(
        () =>
            answerTail.latestAnswerEventId === null ||
            (phoebeBusy && !answerInCurrentTurn)
                ? null
                : {
                      occurrenceId: `committed-event:${answerTail.latestAnswerEventId}`,
                      inProgress: phoebeBusy,
                  },
        [answerTail.latestAnswerEventId, answerInCurrentTurn, phoebeBusy],
    );
    const heldPresentations = useMemo(
        () =>
            holdPresentationsUntilAnswer(
                activeWorkPartition.transcriptOccurrences,
                presentationOwnerIds,
                runActive,
            ),
        [
            activeWorkPartition.transcriptOccurrences,
            presentationOwnerIds,
            runActive,
        ],
    );
    const transcriptOccurrences = useMemo<
        ReadonlyArray<ProjectedActivityInput>
    >(() => {
        return heldPresentations.inputs.flatMap((occurrence) => {
            if (queuedMessageOccurrenceIds.has(occurrence.occurrenceId)) {
                return [];
            }
            if (
                activeQuestionComposer !== null &&
                occurrence.occurrenceId === activeQuestionComposer.ownerId
            ) {
                return [
                    {
                        kind: 'boundary' as const,
                        reason: 'invisible' as const,
                        rowId: `boundary:${occurrence.occurrenceId}`,
                        occurrenceId: occurrence.occurrenceId,
                        eventIds: occurrence.eventIds,
                        day: occurrence.day,
                    },
                ];
            }
            return [occurrence];
        });
    }, [activeQuestionComposer, heldPresentations, queuedMessageOccurrenceIds]);
    // Buttons are offered only while nothing else is asking for the reader:
    // a live run, a pending question (docked or dismissed back into the
    // transcript), or a pending approval all speak for their next action.
    const nextStepsOffer = useMemo(
        () =>
            responsePhase.kind === 'idle' &&
            pendingQuestionOwners.length === 0 &&
            pendingApprovalOwnerIds.length === 0
                ? selectNextStepsOffer(
                      heldPresentations,
                      activityProjection.compoundStateByOwnerOccurrenceId,
                  )
                : null,
        [
            activityProjection.compoundStateByOwnerOccurrenceId,
            heldPresentations,
            pendingApprovalOwnerIds,
            pendingQuestionOwners,
            responsePhase,
        ],
    );
    const nextStepsOfferValue = useMemo<NextStepsOfferValue | null>(
        () =>
            nextStepsOffer === null
                ? null
                : { ...nextStepsOffer, onSelect: requestSendMessage },
        [nextStepsOffer, requestSendMessage],
    );
    // Typed text while buttons are showing is the reader declining them; a
    // button click goes through requestSendMessage and is never an ignore.
    const sendTypedMessage = useCallback(
        (
            content: string,
            activityEventIds: ReadonlyArray<string>,
            uploadIds: ReadonlyArray<string>,
        ): Promise<boolean> | null => {
            const shownCount = nextStepsOffer?.actions.length ?? 0;
            if (shownCount > 0) {
                posthog?.capture(EVENTS.V3_NEXT_STEPS_IGNORED, {
                    count: shownCount,
                });
            }
            // Ids already riding an in-flight message are excluded so a
            // rapid follow-up cannot attach the same shifts twice.
            const staged = (stagedReferences?.references ?? []).filter(
                (reference) => !inFlightStagedIdsRef.current.has(reference.id),
            );
            // Attached context leads the message, so the sent bubble shows
            // the pills up top; the user may already have @-mentioned an
            // entity, in which case its token stays where they typed it.
            let outgoing = content;
            for (const reference of [...staged].reverse()) {
                const token = `[[${reference.type}:${reference.id}]]`;
                if (!outgoing.includes(token)) {
                    outgoing = `${token} ${outgoing}`.trim();
                }
            }
            return sendMessage(
                outgoing,
                activityEventIds,
                uploadIds,
                staged.map((reference) => reference.id),
            );
        },
        [nextStepsOffer, posthog, sendMessage, stagedReferences],
    );
    const groupedOccurrences = useMemo(
        () =>
            groupActivityOccurrences(
                transcriptOccurrences,
                forcedVisibleOwnerIds,
            ),
        [forcedVisibleOwnerIds, transcriptOccurrences],
    );
    // Once the answer is the newest visible content, a generic thinking
    // indicator below it would only lag the terminal event; the run's next
    // step, if any, re-announces itself as a live activity row.
    const answerIsLatestVisible = useMemo(
        () =>
            latestVisibleInputIsAssistantAnswer(groupedOccurrences, runActive),
        [groupedOccurrences, runActive],
    );
    // The stop click is the moment Phoebe stops thinking on screen: while
    // the run winds down, the transcript shows the stop request row and the
    // disabled "Stopping…" control, never a thinking row or a live step.
    const stoppingResponse = responsePhase.kind === 'stopping';
    // Renderers declare when they contain their approval status. Otherwise the petal row speaks: "Thinking…"
    // for a working run no matter how long it runs, a waiting line while
    // Phoebe needs the user, nothing once the answer is the newest visible
    // content or a stop is in flight. The old status strip is gone.
    // The waiting treatment comes from the response phase alone — a working
    // run that is holding for a permission or a reply carries that hold on
    // its phase, so this surface never re-reads raw run-state flags.
    const responseWaitingFor = responsePhaseWaitingFor(responsePhase);
    const waitingForInput = responseWaitingFor !== null;
    const cardsShowApprovalStatus =
        pendingApprovalOwnerIds.length > 0 &&
        pendingApprovalOwnerIds.every((ownerId) => {
            const approval =
                activityProjection.compoundStateByOwnerOccurrenceId.get(
                    ownerId,
                )?.approval;
            return (
                approval?.kind === 'approval_required' &&
                approval.statusPlacement === 'inline'
            );
        });
    let pendingStepCopy: string | null;
    switch (responsePhase.kind) {
        case 'sending':
        case 'working':
        case 'waiting': {
            // A locally superseded card ends the wait before the server
            // confirms it, so the petal must not keep claiming Phoebe waits.
            const waitingFor = interactionLocallySuperseded
                ? null
                : responseWaitingFor;
            pendingStepCopy =
                waitingFor === 'approval' && cardsShowApprovalStatus
                    ? null
                    : waitingFor === 'approval' || waitingFor === 'both'
                      ? APPROVAL_WAITING_COPY
                      : waitingFor === 'user_input'
                        ? 'Waiting for your reply…'
                        : answerIsLatestVisible &&
                            !responseThinkingFollowsAnswer(responsePhase, state)
                          ? null
                          : 'Thinking…';
            break;
        }
        case 'idle':
        case 'needs_attention':
        case 'stopping':
            pendingStepCopy = null;
            break;
        default: {
            const exhaustivePhase: never = responsePhase;
            pendingStepCopy = exhaustivePhase;
        }
    }
    if (queuedMessages.length > 0) {
        // A queued message is not in the transcript yet, so nothing to sit under.
        pendingStepCopy = null;
    }
    const rows = useMemo(() => {
        const attachmentOwnerIds = new Set<string>();
        for (const [
            ownerId,
            compoundState,
        ] of activityProjection.compoundStateByOwnerOccurrenceId) {
            const kind = compoundState.result?.presentation?.kind;
            if (kind === 'chat-attachment') {
                attachmentOwnerIds.add(ownerId);
            }
        }
        const composedRows = composeMessageAttachments(
            projectActivityRows(
                groupedOccurrences,
                runActive && !stoppingResponse,
                pendingStepCopy,
            ),
            attachmentOwnerIds,
            presentationOwnerIds,
        );
        return composedRows.map((row) =>
            row.kind === 'occurrence' &&
            forcedVisibleOwnerIds.has(row.occurrenceId)
                ? { ...row, isCard: true }
                : row,
        );
    }, [
        activityProjection.compoundStateByOwnerOccurrenceId,
        forcedVisibleOwnerIds,
        groupedOccurrences,
        pendingStepCopy,
        presentationOwnerIds,
        runActive,
        stoppingResponse,
    ]);
    const sendFailed = responsePhase.kind === 'needs_attention';
    const presentedRows = useMemo<ReadonlyArray<ProjectedTranscriptRow>>(() => {
        const withStopRows: Array<ProjectedTranscriptRow> = [...rows];
        for (const event of unacknowledgedLocalStopEvents) {
            const stopRow: ProjectedTranscriptRow = {
                kind: 'occurrence',
                rowId: `local-stop-request:${event.id}`,
                occurrenceId: `local-stop-request:${event.id}`,
                eventIds: [],
                node: (
                    <EventLine glyph={<OctagonX />}>
                        {VIEWER_STOP_REQUEST_COPY}
                    </EventLine>
                ),
            };
            // The stop belongs where it was clicked, ahead of anything
            // composed after it. Appending instead puts it below a later
            // message, then moves it up when the server echoes the stop.
            const composedAfter = withStopRows.findIndex((row) => {
                if (row.kind !== 'occurrence') {
                    return false;
                }
                const operationId = row.occurrenceId.startsWith(
                    'local-operation:',
                )
                    ? row.occurrenceId.slice('local-operation:'.length)
                    : null;
                return (
                    operationId !== null &&
                    !event.queuedUserTurnIds.has(operationId)
                );
            });
            if (composedAfter === -1) {
                withStopRows.push(stopRow);
            } else {
                withStopRows.splice(composedAfter, 0, stopRow);
            }
        }
        if (!sendFailed) {
            return withStopRows;
        }
        // The one true error state also rides the petal row, with its
        // recovery action inline. An undelivered send retries in place; the
        // route dedupes on the operation id, so Try again cannot double-send.
        return [
            ...withStopRows,
            {
                kind: 'occurrence',
                rowId: 'response-status:needs-attention',
                occurrenceId: 'response-status:needs-attention',
                eventIds: [],
                node: (
                    <EventLine
                        className="min-w-0"
                        glyph={<PetalTickLoader className="size-[1em]" />}
                    >
                        <span aria-live="polite">
                            Your message didn’t reach Phoebe.
                        </span>{' '}
                        <button
                            type="button"
                            onClick={retryFailedSends}
                            className="text-foreground underline underline-offset-2"
                        >
                            Try again
                        </button>
                    </EventLine>
                ),
            },
        ];
    }, [retryFailedSends, rows, sendFailed, unacknowledgedLocalStopEvents]);
    useLayoutEffect(() => {
        if (positioningState.requestKey !== restorationRequestKey) {
            const resetGroupIds = new Set<string>();
            expandedGroupIdsRef.current = resetGroupIds;
            setExpandedGroupIds(resetGroupIds);
            setPositioningState({
                kind: 'waiting',
                requestKey: restorationRequestKey,
            });
            return;
        }
        if (completedInitialCatchupScopeKey !== restorationScopeKey) {
            return;
        }
        if (positioningState.kind === 'expansion_restored') {
            // The saved expansion has now rebuilt the flattened row order.
            // Only this later commit may expose the anchor to the virtualizer.
            setPositioningState({
                kind: 'restoring',
                requestKey: restorationRequestKey,
                descriptor: positioningState.descriptor,
            });
            return;
        }
        if (positioningState.kind !== 'waiting') {
            return;
        }
        if (
            initialDescriptor === null ||
            !presentedRows.some((row) =>
                rowContainsCommittedEvent(
                    row,
                    initialDescriptor.committedEventId,
                ),
            )
        ) {
            setPositioningState({
                kind: 'normal',
                requestKey: restorationRequestKey,
            });
            return;
        }
        const restoredGroupIds = new Set(initialDescriptor.expandedGroupIds);
        expandedGroupIdsRef.current = restoredGroupIds;
        setExpandedGroupIds(restoredGroupIds);
        setPositioningState({
            kind: 'expansion_restored',
            requestKey: restorationRequestKey,
            descriptor: {
                committedEventId: initialDescriptor.committedEventId,
                intraRowOffset: initialDescriptor.intraRowOffset,
            },
        });
    }, [
        completedInitialCatchupScopeKey,
        initialDescriptor,
        positioningState,
        presentedRows,
        restorationRequestKey,
        restorationScopeKey,
    ]);
    // A paused stream leaves accepted work looking queued with no explanation,
    // which reads as a backend backlog and drives reloads. Say so, but only
    // while the conversation is actually waiting on the stream: an idle chat
    // riding out a brief retry has nothing to explain.
    const waitingOnStream = queuedMessages.length > 0 || runActive;
    const streamPausedCopy = !waitingOnStream
        ? null
        : eventStreamCycleState === 'rate-limited'
          ? 'Phoebe is busy. Reconnecting…'
          : eventStreamCycleState === 'retrying'
            ? 'Reconnecting to Phoebe…'
            : null;
    useEffect(() => {
        activeTransportRef.current = transport;
        transport.start();
        return () => {
            // `stop` publishes synchronously, so close the React state boundary
            // before cleanup asks the transport to release its resources.
            activeTransportRef.current = null;
            transport.stop();
        };
    }, [transport]);

    useEffect(() => {
        if (
            interruptResolvedAuthoritatively ||
            stoppedResponseEnded ||
            stopRequestAcknowledged
        ) {
            STOP_REQUEST_ATTEMPTS.delete(interruptRunKey);
            interruptController.resolveAuthoritatively();
        }
        if (
            responsePhaseLive &&
            interruptController.getSnapshot().kind !== 'already_inactive'
        ) {
            interruptController.reset();
        }
    }, [
        interruptController,
        interruptResolvedAuthoritatively,
        interruptRunKey,
        responsePhaseLive,
        stopRequestAcknowledged,
        stoppedResponseEnded,
    ]);
    // A rejected Stop or an accepted no-op never stopped anything. Drop its
    // local row once the request guard is released so the transcript does
    // not claim a stop the server never enqueued.
    useEffect(() => {
        if (
            interruptState.kind !== 'definitively_rejected' &&
            interruptState.kind !== 'already_inactive'
        ) {
            return;
        }
        setLocalStopRequestEvents((events) => {
            const retainedEvents = events.filter(
                (event) =>
                    event.runKey !== interruptRunKey ||
                    stopProjections.byRequestId.get(event.requestId)?.echoed ===
                        true,
            );
            return retainedEvents.length === events.length
                ? events
                : retainedEvents;
        });
    }, [interruptRunKey, interruptState.kind, stopProjections]);

    useEffect(() => {
        if (eventStreamCycleState === 'hidden') {
            // A hidden tab need not tick anything.
            return;
        }
        // The stop deadline and the thinking-evidence transition defer to an
        // in-flight authoritative response, so they sit out a draining poll.
        let thinkingRemainingMs: number | null = null;
        let interruptRemainingMs: number | null = null;
        if (eventStreamCycleState !== 'draining') {
            const catchUpComplete = !visibilityCatchUpRequiredRef.current;
            interruptController.evaluateDeadline(catchUpComplete);
            thinkingRemainingMs =
                thinkingEvidenceTransitionInMs(thinkingEvidence);
            interruptRemainingMs =
                catchUpComplete &&
                (interruptState.kind === 'pending' ||
                    interruptState.kind === 'accepted')
                    ? interruptController.deadlineRemainingMs()
                    : null;
        }
        const remainingTimesMs = [
            thinkingRemainingMs,
            interruptRemainingMs,
        ].filter((remainingMs): remainingMs is number => remainingMs !== null);
        if (remainingTimesMs.length === 0) {
            return;
        }
        const handle = window.setTimeout(
            () => {
                setForegroundTimeRevision((current) => current + 1);
            },
            Math.ceil(Math.min(...remainingTimesMs)),
        );
        return () => window.clearTimeout(handle);
    }, [
        eventStreamCycleState,
        foregroundTimeRevision,
        interruptController,
        interruptState.kind,
        store.foregroundClock,
        thinkingEvidence,
    ]);

    const transcriptEmpty =
        activeQuestionComposer === null &&
        rows.length === 0 &&
        queuedMessages.length === 0 &&
        activeWork.length === 0 &&
        responsePhase.kind !== 'needs_attention';
    // Until the first catch-up lands, nothing is known about the run, so the
    // frame shows the loader rather than a home stage that may be wrong.
    const awaitingInitialCatchup =
        completedInitialCatchupScopeKey !== restorationScopeKey;
    let transcript: ReactNode;
    if (transcriptEmpty && awaitingInitialCatchup) {
        transcript = <ConversationLoading />;
    } else if (transcriptEmpty) {
        transcript = profile.emptyState(stableScope);
    } else {
        transcript = (
            <TableSelectionContext.Provider value={tableSelectionStore}>
                <ApprovalStackContext.Provider value={approvalStack}>
                    <VirtualizedTranscript
                        key={restorationRequestKey}
                        captureRef={transcriptCaptureRef}
                        initialDescriptor={activeTranscriptDescriptor}
                        onGroupToggle={requestGroupToggle}
                        onPositionChange={handleTranscriptPositionChange}
                        rows={presentedRows}
                        waitingForInput={waitingForInput}
                    />
                </ApprovalStackContext.Provider>
            </TableSelectionContext.Provider>
        );
    }

    const interruptRecoveryVisible =
        presentedInterruptState.kind === 'ambiguous' ||
        presentedInterruptState.kind === 'definitively_rejected';
    // The idle stop offer has no place on the empty chat home, and once the
    // server durably holds a stop the mic returns while the run winds down.
    // A failed stop keeps its recovery controls visible regardless.
    const interruptOffered =
        profile.features.interrupt.kind === 'supported' &&
        responsePhase.kind !== 'needs_attention' &&
        (responseStopEligible ||
            responsePhase.kind === 'stopping' ||
            interruptRecoveryVisible);
    const stopRequestable =
        presentedInterruptState.kind !== 'authoritatively_resolved' &&
        presentedInterruptState.kind !== 'already_inactive';
    const stopRequestPending = presentedInterruptState.kind === 'pending';
    // This occupies the mic slot while a response can still be interrupted.
    const stopControl = interruptOffered && stopRequestable && (
        <button
            type="button"
            aria-label={stopRequestPending ? 'Stopping…' : 'Stop response'}
            disabled={stopRequestPending}
            onClick={requestStopResponse}
            className="text-foreground hover:bg-accent focus-visible:outline-agent-action grid size-[30px] shrink-0 place-items-center rounded-md transition-colors duration-150 outline-none focus-visible:outline-2 focus-visible:outline-offset-2 disabled:opacity-[0.42]"
        >
            <span
                aria-hidden="true"
                className="bg-foreground size-3 rounded-[3px]"
            />
        </button>
    );
    const interruptChromeVisible =
        interruptOffered &&
        (interruptRecoveryVisible || profile.features.composer.kind !== 'text');
    // A const alias keeps the discriminant narrowing available inside the
    // docked question's renderComposer closure.
    const composerFeature = profile.features.composer;
    // The composer is never hidden; it is disabled with a visible reason
    // when it cannot send. This is the only condition that ever removed it.
    const textComposerVisible = composerFeature.kind === 'text';

    return (
        <HarnessScopeContext.Provider value={stableScope}>
            <EntityReferenceLabelsProvider scope={stableScope}>
                <LatestAssistantOccurrenceContext.Provider
                    value={latestAssistantAnswer}
                >
                    <NextStepsOfferContext.Provider value={nextStepsOfferValue}>
                        <section
                            aria-label="Agent chat"
                            className="@container/v3-chat mx-auto flex h-full min-h-0 w-full min-w-0 flex-col"
                        >
                            <div className="min-h-0 min-w-0 flex-1 overflow-hidden">
                                {transcript}
                            </div>
                            {connectionFailureCopy !== null && (
                                <div
                                    role="alert"
                                    className={cn(
                                        CHAT_COLUMN_CLASS,
                                        'mt-3 flex flex-col items-start gap-2',
                                    )}
                                >
                                    <p>{connectionFailureCopy}</p>
                                    <Button
                                        type="button"
                                        variant="outline"
                                        onClick={reloadPage}
                                    >
                                        Reload
                                    </Button>
                                </div>
                            )}
                            {interruptChromeVisible && (
                                <div
                                    className={cn(
                                        CHAT_COLUMN_CLASS,
                                        'mt-3 flex flex-col items-start gap-2',
                                    )}
                                >
                                    {profile.features.composer.kind !==
                                        'text' && stopControl}
                                    {presentedInterruptState.kind ===
                                        'definitively_rejected' && (
                                        <p>
                                            Phoebe could not stop this response.
                                        </p>
                                    )}
                                    {presentedInterruptState.kind ===
                                        'ambiguous' && (
                                        <Fragment>
                                            <p>
                                                We could not confirm whether the
                                                response stopped.
                                            </p>
                                            <Button
                                                type="button"
                                                variant="outline"
                                                onClick={
                                                    refreshAuthoritativeState
                                                }
                                            >
                                                Check again
                                            </Button>
                                        </Fragment>
                                    )}
                                </div>
                            )}
                            {textComposerVisible && (
                                <ComposerFooter>
                                    {queuedMessages.length > 0 && (
                                        <div className={CHAT_COLUMN_CLASS}>
                                            <QueuedMessages
                                                messages={queuedMessages}
                                            />
                                        </div>
                                    )}
                                    {streamPausedCopy !== null && (
                                        <p
                                            className={cn(
                                                'text-muted-foreground mb-2 text-v3-chat-meta',
                                                CHAT_COLUMN_CLASS,
                                            )}
                                        >
                                            {streamPausedCopy}
                                        </p>
                                    )}
                                    {composerDisabledReason !== null && (
                                        // This strip names every disabled reason
                                        // visibly.
                                        <p
                                            id="v3-chat-composer-disabled-reason"
                                            className={cn(
                                                'text-muted-foreground mb-2 text-v3-chat-meta',
                                                CHAT_COLUMN_CLASS,
                                            )}
                                        >
                                            {composerDisabledReason}
                                        </p>
                                    )}
                                    <CardResize>
                                        {activeQuestionComposer === null ? (
                                            <Composer
                                                autoFocus={visible}
                                                contextAttachments={
                                                    <Fragment>
                                                        {stagedReferences?.tray}
                                                        <TableSelectionAttachments
                                                            store={
                                                                tableSelectionStore
                                                            }
                                                        />
                                                    </Fragment>
                                                }
                                                disabled={
                                                    composerDisabledReason !==
                                                    null
                                                }
                                                onTyping={notifyChatTyping}
                                                disabledReasonId={
                                                    composerDisabledReason ===
                                                    null
                                                        ? undefined
                                                        : 'v3-chat-composer-disabled-reason'
                                                }
                                                displayName={
                                                    profile.displayName
                                                }
                                                draftState={composerDraftState}
                                                feature={composerFeature}
                                                onSend={sendTypedMessage}
                                                referenceSearch={{
                                                    organizationId:
                                                        stableScope.organizationId,
                                                    organizationTimeZone:
                                                        stableScope.organizationTimeZone,
                                                    mode: stableScope.mode,
                                                    ...(referenceSearchOutreachId ===
                                                    undefined
                                                        ? {}
                                                        : {
                                                              outreachId:
                                                                  referenceSearchOutreachId,
                                                          }),
                                                }}
                                                placeholder={
                                                    transcriptEmpty
                                                        ? undefined
                                                        : `Message ${profile.displayName}…`
                                                }
                                                presetsVisible={
                                                    completedInitialCatchupScopeKey ===
                                                        restorationScopeKey &&
                                                    transcriptEmpty &&
                                                    (stagedReferences ===
                                                        undefined ||
                                                        stagedReferences
                                                            .references
                                                            .length === 0)
                                                }
                                                controls={composerControls}
                                                initialMessage={initialMessage}
                                                prefill={composerPrefill}
                                                trailingControls={stopControl}
                                                uploads={
                                                    uploadsEnabled
                                                        ? uploadAttachments
                                                        : undefined
                                                }
                                                voiceInput={{
                                                    flow:
                                                        voiceInputFlow ??
                                                        'agent_chat',
                                                    organizationId:
                                                        stableScope.organizationId,
                                                }}
                                            />
                                        ) : (
                                            // A docked question keeps the transcript's reading
                                            // column: hoisting it into the footer otherwise
                                            // stretches the card to the full frame width while
                                            // the messages above it stay at 720px.
                                            <motion.div
                                                key={`question-composer:${activeQuestionComposer.ownerId}`}
                                                className={CHAT_COLUMN_CLASS}
                                                initial={
                                                    reduceMotion === true
                                                        ? false
                                                        : {
                                                              opacity: 0,
                                                              y: 6,
                                                          }
                                                }
                                                animate={{
                                                    opacity: 1,
                                                    y: 0,
                                                }}
                                                transition={{
                                                    duration:
                                                        reduceMotion === true
                                                            ? 0
                                                            : 0.2,
                                                    ease: [0.16, 1, 0.3, 1],
                                                }}
                                            >
                                                <QuestionDockContext.Provider
                                                    value={{
                                                        dismiss: () =>
                                                            setDismissedQuestionOwnerId(
                                                                activeQuestionComposer.ownerId,
                                                            ),
                                                        renderComposer: (
                                                            dockOptions,
                                                        ) => (
                                                            <Composer
                                                                contextAttachments={
                                                                    <TableSelectionAttachments
                                                                        store={
                                                                            tableSelectionStore
                                                                        }
                                                                    />
                                                                }
                                                                disabled={
                                                                    composerDisabledReason !==
                                                                    null
                                                                }
                                                                disabledReasonId={
                                                                    composerDisabledReason ===
                                                                    null
                                                                        ? undefined
                                                                        : 'v3-chat-composer-disabled-reason'
                                                                }
                                                                displayName={
                                                                    profile.displayName
                                                                }
                                                                draftState={
                                                                    composerDraftState
                                                                }
                                                                feature={
                                                                    composerFeature
                                                                }
                                                                layout="flush"
                                                                onTyping={
                                                                    notifyChatTyping
                                                                }
                                                                onSend={(
                                                                    content,
                                                                ) =>
                                                                    submitSelectedMessage(
                                                                        content,
                                                                        // Question answers have no UI-context field.
                                                                        (
                                                                            message,
                                                                        ) =>
                                                                            dockOptions.onSendText(
                                                                                message.content,
                                                                            ),
                                                                        true,
                                                                    )
                                                                }
                                                                restoreDraftOnSendFailure={
                                                                    true
                                                                }
                                                                referenceSearch={{
                                                                    organizationId:
                                                                        stableScope.organizationId,
                                                                    organizationTimeZone:
                                                                        stableScope.organizationTimeZone,
                                                                    mode: stableScope.mode,
                                                                    ...(referenceSearchOutreachId ===
                                                                    undefined
                                                                        ? {}
                                                                        : {
                                                                              outreachId:
                                                                                  referenceSearchOutreachId,
                                                                          }),
                                                                }}
                                                                placeholder={
                                                                    dockOptions.placeholder ??
                                                                    `Respond to ${profile.displayName}’s question or ask something else…`
                                                                }
                                                                controls={
                                                                    composerControls
                                                                }
                                                                trailingControls={
                                                                    stopControl
                                                                }
                                                                voiceInput={{
                                                                    flow:
                                                                        voiceInputFlow ??
                                                                        'agent_chat',
                                                                    organizationId:
                                                                        stableScope.organizationId,
                                                                }}
                                                            />
                                                        ),
                                                    }}
                                                >
                                                    {
                                                        activeQuestionComposer.node
                                                    }
                                                </QuestionDockContext.Provider>
                                            </motion.div>
                                        )}
                                    </CardResize>
                                    {chatTypingUsers.length > 0 && (
                                        // px-9 = the column's px-5 gutter plus the
                                        // composer card's px-4 inset, so the label
                                        // lines up with the composer's own text at
                                        // any chat width (both paddings are fixed).
                                        <p
                                            className={cn(
                                                CHAT_COLUMN_CLASS,
                                                'text-muted-foreground mt-1.5 px-9 text-v3-chat-meta',
                                            )}
                                            data-testid="v3-chat-typing-indicator"
                                        >
                                            {formatChatTypingLabel(
                                                chatTypingUsers,
                                            )}
                                        </p>
                                    )}
                                </ComposerFooter>
                            )}
                        </section>
                    </NextStepsOfferContext.Provider>
                </LatestAssistantOccurrenceContext.Provider>
            </EntityReferenceLabelsProvider>
        </HarnessScopeContext.Provider>
    );
}
